import { DownloadIcon, EditIcon } from "@xola/icons";
import { SecondaryButton } from "common";
import { isArray, isObject } from "lodash";
import React from "react";
import { Audit, Permission, Purchase, PurchaseItem, Waiver, WaiverParticipant } from "types";
import { useResource } from "../../../../hooks/useResource";
import { getWaiverFileName, getWaiverUrl } from "../../../../models/Waiver";
import { openAuthenticatedUrl } from "../../../../utils/url";
import { IsGranted } from "../../../Common/IsGranted";
import { TimelineItem } from "../TimelineItem";

interface WaiverSignedTimelineItemProps {
    purchase: Purchase;
    item: PurchaseItem;
    audit: Audit;
    showDate?: boolean;
}

interface WaiverDataProps {
    id: string;
}

export const WaiverSignedTimelineItem = ({
    purchase,
    item,
    audit,
    showDate = false,
}: WaiverSignedTimelineItemProps) => {
    const isWaiverSigned =
        audit.objectClass === "purchase" && WaiverSignedTimelineItem.supportsAudit(purchase, item, audit);
    const isWaiverUpdated = audit.objectClass === "waiver";

    if (!isWaiverSigned && !isWaiverUpdated) {
        return null;
    }

    const waivers = isWaiverSigned ? getWaivers(item, audit) : [{ id: audit.objectId }]; // For waiver updates, use the objectId as the waiver ID

    const title = isWaiverUpdated ? "Waiver Updated" : "Waiver Signed";

    return (
        <IsGranted permissions={Permission.WAIVER_TIMELINE_VIEW}>
            <TimelineItem
                title={title}
                audit={audit}
                icon={<EditIcon />}
                purchase={purchase}
                color="primary"
                showDate={showDate}
            >
                <ul>
                    {waivers.map((waiverData: WaiverDataProps) => (
                        <WaiverItem key={waiverData.id} waiverData={waiverData} />
                    ))}
                </ul>
            </TimelineItem>
        </IsGranted>
    );
};

const WaiverItem = ({ waiverData }: { waiverData: WaiverDataProps }) => {
    const { data: waiver } = useResource<Waiver>(["waivers", waiverData.id]);

    const handleDownloadButtonClick = () => {
        if (waiver) {
            openAuthenticatedUrl(getWaiverUrl(waiver), getWaiverFileName(waiver));
        }
    };

    return (
        <>
            <ul>
                {waiver?.participants.map((participant: WaiverParticipant) => (
                    <li key={participant.id} className="no-translate">
                        {participant.customerName}
                        &nbsp;
                        {participant.customerEmail ? (
                            <span className="text-gray-darker">({participant.customerEmail})</span>
                        ) : null}
                    </li>
                ))}
            </ul>

            <div className="font-bold">
                <SecondaryButton size="tiny" className="py-2 px-3" onClick={handleDownloadButtonClick}>
                    <DownloadIcon size="tiny" /> <span className="ml-2">Download</span>
                </SecondaryButton>
            </div>
        </>
    );
};

WaiverSignedTimelineItem.supportsAudit = (_purchase: Purchase, item: PurchaseItem, audit: Audit) => {
    const newWaivers = getWaivers(item, audit);
    return newWaivers?.length > 0;
};

const getWaivers = (item: PurchaseItem, audit: Audit) => {
    if (!audit.data?.items) {
        return [];
    }

    const auditItem =
        isArray(audit.data?.items) &&
        audit.data.items.find((auditItem: any) => auditItem.id === item?.id && auditItem.waivers);

    if (!auditItem) {
        return [];
    }

    // When first waiver is created, the audit data will have
    // waivers: { old: null, new:[{id: ID}]} but for the next waiver
    // signed audits data will be in this format
    // waivers: { 1: {old, new: {id: ID}}}
    // we are handling both the cases.
    if (auditItem.waivers.new) {
        // The 'new' value will be an array
        // If auditItem.waivers.old exists, return only the newly signed waivers
        if (auditItem.waivers.old) {
            const oldWaiverIds = new Set(auditItem.waivers.old.map((waiver: { id: string }) => waiver.id));
            return auditItem.waivers.new.filter((waiver: { id: string }) => !oldWaiverIds.has(waiver.id));
        }

        return auditItem.waivers.new;
    }

    if (isObject(auditItem.waivers)) {
        // Waivers id in this if block will be in format of new: {id: ID}
        const auditWaivers: Array<{ id: ID }> = [];
        for (const waiverItem of Object.values(auditItem.waivers) as any) {
            // Add to timeline only if New waiver is signed
            if (waiverItem.new && waiverItem.old === null) {
                auditWaivers.push(waiverItem.new);
            }
        }

        return auditWaivers;
    }

    return [];
};
